/**
 * map-tiles — the ONE place a Leaflet basemap comes from (#1574).
 *
 * Every map on The Grid rendered OpenStreetMap's "Access blocked — App is not
 * following the tile usage policy" tile. OSM's public tile server forbids
 * production apps: it bans by referrer and requires an identifying User-Agent
 * a browser will not let us set. Three dispatch surfaces hardcoded that URL
 * (`dispatch/map.tsx`, `dispatch/route-rail.tsx`, `dispatch/route-planner.tsx`)
 * while three others already used the shared Esri helper and were never broken.
 *
 * Two basemaps, because the two jobs are different:
 *
 *   · `satellite` — "is the backflow behind that building?" The field and site
 *     surfaces have always used Esri World Imagery and keep it.
 *   · `street` — "how do I drive this route?" Aerial imagery has no street
 *     names, so routing needs labels.
 *
 * The street basemap is configurable so a keyed provider (MapTiler, Stadia,
 * Mapbox — all have free tiers that cover this volume) can be pointed at
 * without a code change: set `VITE_MAP_TILE_URL` with the key already in the
 * template, plus `VITE_MAP_TILE_ATTR` for the credit the provider's terms
 * require. These are Vite build-time vars, inlined into the bundle like
 * `VITE_STRIPE_PUBLISHABLE_KEY`, so changing one needs a rebuild, and whatever
 * goes in the URL is PUBLIC — use a domain-restricted key, never a secret one.
 *
 * ## What unset means, and why it changed (#1626)
 *
 * With nothing configured the street basemap is Esri World Street Map. It was
 * briefly Esri World Imagery — the satellite layer — on the reasoning that it
 * was already proven in production, so dispatch maps would DRAW rather than
 * show a block tile even before anyone set a key. That reasoning was about not
 * falling back to OSM and it still holds; what it missed is that Esri serves a
 * keyless STREET map from the same host, so "draws without a key" and "has
 * street names" were never actually in tension. Routing over aerial imagery
 * with no labels is the thing #1574 set out to avoid.
 *
 * Neither fallback is ever OSM. That is the one rule here that is not a
 * preference: OSM's public server refuses production traffic outright.
 *
 * Honest limitation: this is still a public tile server we hold no contract
 * with, which is the shape of what OSM did to us. Esri has served our satellite
 * layer in production for months and does not enforce by referrer, but "it
 * works today" was true of OSM too. The durable answer is a keyed provider —
 * one env var away, above.
 *
 * And it cannot be checked from here: the build sandbox's network policy
 * answers 403 to CONNECT for `server.arcgisonline.com`, so neither CI nor a
 * build session can prove a tile server serves us. World Street Map was
 * confirmed rendering from a real browser (`threads/dispatch-street-basemap/
 * mockup.html`, Corbin, 2026-09-22) before this default was changed.
 */

// Type-only: erased at compile time, so this module never pulls Leaflet into
// a bundle or a test that does not already have it.
import type { LayerGroup, Map as LeafletMap, TileLayer, TileLayerOptions } from "leaflet";

export type Basemap = "street" | "satellite";

export interface TileSource {
    url: string;
    attribution: string;
    maxZoom: number;
}

/** Esri World Imagery — in production since before #1574, no key required. */
export const SATELLITE_TILES: TileSource = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri World Imagery",
    maxZoom: 22,
};

/**
 * Esri World Street Map — the keyless street basemap, same host as the
 * satellite layer, and what an unconfigured `street` basemap resolves to.
 *
 * Capped at 19, the same ceiling a configured provider gets by default. The
 * cap is deliberately conservative rather than measured: the build environment
 * cannot reach this host, so how deep the service really tiles is not something
 * that could be checked here, and over-declaring it means a dispatcher zooms
 * into grey squares with nothing to explain why. Under-declaring costs a couple
 * of zoom levels on a screen used to read a route, not to inspect a valve —
 * and a configured provider sets its own ceiling via VITE_MAP_TILE_MAX_ZOOM.
 */
export const STREET_TILES: TileSource = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri World Street Map",
    maxZoom: 19,
};

/** Leaflet substitutes these; a template missing any of them cannot render. */
const REQUIRED_PLACEHOLDERS = ["{z}", "{x}", "{y}"] as const;

/** Vite's `import.meta.env`, narrowed so this module stays unit-testable. */
export interface TileEnv {
    VITE_MAP_TILE_URL?: string;
    VITE_MAP_TILE_ATTR?: string;
    VITE_MAP_TILE_MAX_ZOOM?: string;
}

function parseMaxZoom(raw: string | undefined, fallback: number): number {
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 && n <= 24 ? n : fallback;
}

/**
 * The configured street basemap, or the keyless Esri street fallback.
 *
 * A URL that is blank, not https, or missing a Leaflet placeholder is treated
 * as unconfigured: a typo in a Render env var should cost you the provider you
 * paid for, not leave the dispatcher staring at a grey grid with no way to tell
 * why. Falling back to a working street map means a bad key degrades the
 * quality of the map rather than the usability of the screen.
 */
export function resolveStreetTiles(env: TileEnv = {} as TileEnv): TileSource {
    const url = env.VITE_MAP_TILE_URL?.trim();
    if (!url) return STREET_TILES;
    if (!/^https:\/\//i.test(url)) return STREET_TILES;
    if (!REQUIRED_PLACEHOLDERS.every((p) => url.includes(p))) return STREET_TILES;

    const attribution = env.VITE_MAP_TILE_ATTR?.trim();
    return {
        url,
        // Most providers' terms require a credit; say so rather than silently
        // shipping an empty one.
        attribution: attribution || "Map tiles",
        maxZoom: parseMaxZoom(env.VITE_MAP_TILE_MAX_ZOOM, 19),
    };
}

/** The tile source for a basemap, reading the live Vite env. */
export function tileSourceFor(basemap: Basemap): TileSource {
    if (basemap === "satellite") return SATELLITE_TILES;
    return resolveStreetTiles(import.meta.env as unknown as TileEnv);
}

/**
 * Add the basemap to a Leaflet map. `L` is passed in so this module never
 * imports Leaflet itself and stays testable in a plain node environment.
 */
/** The slice of Leaflet this module uses. */
export interface LeafletTileApi {
    tileLayer(url: string, options?: TileLayerOptions): TileLayer;
}

export function addBasemapLayer(
    L: LeafletTileApi,
    map: LeafletMap | LayerGroup,
    basemap: Basemap,
    opts: { maxZoom?: number } = {},
): void {
    const src = tileSourceFor(basemap);
    L.tileLayer(src.url, {
        attribution: src.attribution,
        // A surface may cap zoom below what the provider serves (the pin
        // picker stops at 20); it may never raise it past the source.
        maxZoom: Math.min(opts.maxZoom ?? src.maxZoom, src.maxZoom),
    }).addTo(map);
}
